import { EntityDefinition } from "src/modules/sm-base/models/entity-definition.model";
import { Objects } from "src/modules/utils/shared/objects";
import { CalendarEvent } from "../netgen/CalendarEvent";
import { CalendarEventTemplate } from "../netgen/CalendarEventTemplate";
import { CalendarEventType } from "../netgen/CalendarEventType";
import { CalendarOutlineLevel } from "../netgen/CalendarOutlineLevel";
import { CalendarOutlineTemplate } from "../netgen/CalendarOutlineTemplate";
import { CalendarRole } from "../netgen/CalendarRole";
import { Project } from "../netgen/Project";
import { ProjectCustomFieldDefinition } from "../netgen/ProjectCustomFieldDefinition";
import { ProjectTemplate } from "../netgen/ProjectTemplate";
import { ProjectType } from "../netgen/ProjectType";
import { NetworkLinkedDocument } from "src/modules/enaio/netgen/NetworkLinkedDocument";
import { NetworkLinkedDocumentCategory } from "src/modules/enaio/netgen/NetworkLinkedDocumentCategory";
import { ResourceType } from "../netgen/ResourceType";
import { Resource } from "../netgen/Resource";
import { DatabaseUser } from "src/modules/database/shared/database-user.entity";
import { FrontendField } from "src/modules/sm-base/netgen/FrontendField";
import { FrontendFieldListItem } from "src/modules/sm-base/netgen/FrontendFieldListItem";
import { FrontendFieldType } from "src/modules/sm-base/netgen/FrontendFieldType";
import { FrontendForm } from "src/modules/sm-base/netgen/FrontendForm";
import { Utils } from "src/modules/utils/shared/utils";
import { CalendarEventReminderType } from "../netgen/CalendarEventReminderType";
import { MainAppService } from "src/modules/app-template/services/main-app.service";

type ReminderDeltaUnit = "minutes" | "hours" | "days" | "weeks";

export class ProjectPlanningTools {
    static definitionCalendarEvent = Objects.fromPlain(EntityDefinition<CalendarEvent>, {
        type: CalendarEvent,
        nameKey: "entity.CalendarEvent.entityName",
        namePluralKey: "entity.CalendarEvent.entityNamePlural",
        categoryKey: "ProjectPlanning.adminCategoryEvents",
        icon: "fas fa-font-awesome",
        apiEndpoint: "api/projectplanning/calendarEvent"
    });

    static definitionCalendarEventTemplate = Objects.fromPlain(EntityDefinition<CalendarEventTemplate>, {
        type: CalendarEventTemplate,
        nameKey: "entity.CalendarEventTemplate.entityName",
        namePluralKey: "entity.CalendarEventTemplate.entityNamePlural",
        categoryKey: "ProjectPlanning.adminCategoryTasks",
        icon: "fas fa-calendar",
        apiEndpoint: "api/projectplanning/calendarEventTemplate",
        adminListUrl: "/project-planning/admin/edit-calendar-event-templates"
    });

    static definitionCalendarEventType = Objects.fromPlain(EntityDefinition<CalendarEventType>, {
        type: CalendarEventType,
        nameKey: "entity.CalendarEventType.entityName",
        namePluralKey: "entity.CalendarEventType.entityNamePlural",
        categoryKey: "ProjectPlanning.adminCategoryTasks",
        icon: "fas fa-font-awesome",
        apiEndpoint: "api/projectplanning/calendarEventType",
        adminListUrl: "/project-planning/admin/edit-calendar-event-types"
    });
    
    static definitionCalendarOutlineLevel = Objects.fromPlain(EntityDefinition<CalendarOutlineLevel>, {
        type: CalendarOutlineLevel,
        nameKey: "entity.CalendarOutlineLevel.entityName",
        namePluralKey: "entity.CalendarOutlineLevel.entityNamePlural",
        categoryKey: "ProjectPlanning.adminCategoryOutline",
        icon: "fas fa-font-awesome",
        apiEndpoint: "api/projectplanning/calendarOutlineLevel",
        adminListUrl: "/project-planning/admin/edit-calendar-outline-levels"
    });
    
    static definitionCalendarOutlineTemplate = Objects.fromPlain(EntityDefinition<CalendarOutlineTemplate>, {
        type: CalendarOutlineTemplate,
        nameKey: "entity.CalendarOutlineTemplate.entityName",
        namePluralKey: "entity.CalendarOutlineTemplate.entityNamePlural",
        categoryKey: "ProjectPlanning.adminCategoryOutline",
        icon: "fas fa-folder-tree",
        apiEndpoint: "api/projectplanning/calendarOutlineTemplate",
        adminListUrl: "/project-planning/admin/edit-calendar-outline-templates"
    });

    static definitionProjectCustomFieldDefinition = Objects.fromPlain(EntityDefinition<ProjectCustomFieldDefinition>, {
        type: ProjectCustomFieldDefinition,
        nameKey: "entity.ProjectCustomFieldDefinition.entityName",
        namePluralKey: "entity.ProjectCustomFieldDefinition.entityNamePlural",
        categoryKey: "ProjectPlanning.adminCategoryProjects",
        icon: "fas fa-font-awesome",
        apiEndpoint: "api/projectplanning/projectCustomFieldDefinition",
        adminListUrl: "/project-planning/admin/edit-project-custom-field-definitions"
    });

    static definitionProjectTemplate = Objects.fromPlain(EntityDefinition<ProjectTemplate>, {
        type: ProjectTemplate,
        nameKey: "entity.ProjectTemplate.entityName",
        namePluralKey: "entity.ProjectTemplate.entityNamePlural",
        categoryKey: "ProjectPlanning.adminCategoryProjects",
        icon: "fas fa-folder-tree",
        apiEndpoint: "api/projectplanning/projectTemplate",
        adminListUrl: "/project-planning/admin/edit-project-templates"
    });

    static definitionProject = Objects.fromPlain(EntityDefinition<Project>, {
        type: Project,
        nameKey: "entity.Project.entityName",
        namePluralKey: "entity.Project.entityNamePlural",
        categoryKey: "ProjectPlanning.adminCategoryProjects",
        icon: "fas fa-calendar",
        apiEndpoint: "api/projectplanning/project",
        adminListUrl: "/project-planning/admin/edit-projects"
    });

    static definitionProjectType = Objects.fromPlain(EntityDefinition<ProjectType>, {
        type: ProjectType,
        nameKey: "entity.ProjectType.entityName",
        namePluralKey: "entity.ProjectType.entityNamePlural",
        categoryKey: "ProjectPlanning.adminCategoryProjects",
        icon: "fas fa-calendar",
        apiEndpoint: "api/projectplanning/projectType",
        adminListUrl: "/project-planning/admin/edit-project-types"
    });

    static definitionCalendarRole = Objects.fromPlain(EntityDefinition<CalendarRole>, {
        type: CalendarRole,
        nameKey: "entity.CalendarRole.entityName",
        namePluralKey: "entity.CalendarRole.entityNamePlural",
        categoryKey: "ProjectPlanning.adminCategoryUsers",
        icon: "fas fa-font-awesome",
        apiEndpoint: "api/projectplanning/calendarRole",
        adminListUrl: "/project-planning/admin/edit-calendar-roles"
    });

    static definitionResourceType = Objects.fromPlain(EntityDefinition<ResourceType>, {
        type: ResourceType,
        nameKey: "entity.ResourceType.entityName",
        namePluralKey: "entity.ResourceType.entityNamePlural",
        categoryKey: "ProjectPlanning.adminCategoryResources",
        icon: "fas fa-calendar",
        apiEndpoint: "api/projectplanning/resourceType",
        adminListUrl: "/project-planning/admin/edit-resource-types"
    });
    
    static definitionResource = Objects.fromPlain(EntityDefinition<Resource>, {
        type: Resource,
        nameKey: "entity.Resource.entityName",
        namePluralKey: "entity.Resource.entityNamePlural",
        categoryKey: "ProjectPlanning.adminCategoryResources",
        icon: "fas fa-calendar",
        apiEndpoint: "api/projectplanning/resource",
        adminListUrl: "/project-planning/admin/edit-resources"
    });
    
    static definitionNetworkLinkedDocument = Objects.fromPlain(EntityDefinition<NetworkLinkedDocument>, {
        type: NetworkLinkedDocument,
        nameKey: "entity.NetworkLinkedDocument.entityName",
        namePluralKey: "entity.NetworkLinkedDocument.entityNamePlural",
        apiEndpoint: "api/app/networkLinkedDocument"
    });

    static definitionNetworkLinkedDocumentCategory = Objects.fromPlain(EntityDefinition<NetworkLinkedDocumentCategory>, {
        type: NetworkLinkedDocumentCategory,
        nameKey: "entity.NetworkLinkedDocumentCategory.entityName",
        namePluralKey: "entity.NetworkLinkedDocumentCategory.entityNamePlural",
        categoryKey: "general.adminCategoryDocuments",
        icon: "fas fa-folder-tree",
        apiEndpoint: "api/app/networkLinkedDocumentCategory",
        adminListUrl: "/project-planning/admin/edit-network-linked-document-categories"
    });

    static isUserAtLeastController(user: DatabaseUser): boolean {
        return user.isAdmin || user.getAttributeValue("projectAdministrationRole") == "C";
    }

    static isUserAtLeastEmployee(user: DatabaseUser): boolean {
        return this.isUserAtLeastController(user) || user.getAttributeValue("projectAdministrationRole") == "E";
    }

    static userCanAccessPlanTime(user: DatabaseUser): boolean {
        return this.isUserAtLeastController(user);
    }

    private static updatingReminderDeltaForms = new WeakSet<FrontendForm>();

    static createReminderFields(app: MainAppService, getForm: () => FrontendForm): FrontendField[] {
        return [
            new FrontendField("reminderType", "", FrontendFieldType.dropdown, {
                mandatory: true,
                guiGroup: "reminderType",
                guiWidthInGroup: 30,
                listItems: FrontendFieldListItem.make(Utils.getEnumValues(CalendarEventReminderType), item => [item, CalendarEventReminderType.getDisplayName(item, app)], false),
                onValueChanged: () => ProjectPlanningTools.updateReminderDeltaVisibility(getForm()),
                callValueChangedAfterLoad: true
            }),
            new FrontendField("reminderDeltaMinutes", "", FrontendFieldType.int, {
                visible: false,
                onValueChanged: () => ProjectPlanningTools.reminderDeltaMinutesChanged(getForm()),
                callValueChangedAfterLoad: true
            }),
            new FrontendField("reminderDeltaAmount", "", FrontendFieldType.int, {
                noMapping: true,
                guiGroup: "reminderType",
                guiWidthInGroup: 13,
                hasLabel: false,
                minValue: 0,
                onValueChanged: () => ProjectPlanningTools.reminderDeltaGuiChanged(getForm())
            }),
            new FrontendField("reminderDeltaUnit", "", FrontendFieldType.dropdown, {
                noMapping: true,
                guiGroup: "reminderType",
                guiWidthInGroup: 22,
                hasLabel: false,
                listItems: [
                    new FrontendFieldListItem("minutes" as ReminderDeltaUnit, app.t("entity.CalendarEventTemplate.other.reminderDeltaMinutes")),
                    new FrontendFieldListItem("hours" as ReminderDeltaUnit, app.t("entity.CalendarEventTemplate.other.reminderDeltaHours")),
                    new FrontendFieldListItem("days" as ReminderDeltaUnit, app.t("entity.CalendarEventTemplate.other.reminderDeltaDays")),
                    new FrontendFieldListItem("weeks" as ReminderDeltaUnit, app.t("entity.CalendarEventTemplate.other.reminderDeltaWeeks"))
                ],
                onValueChanged: () => ProjectPlanningTools.reminderDeltaGuiChanged(getForm())
            }),
            new FrontendField("reminderDeltaDirection", "", FrontendFieldType.dropdown, {
                noMapping: true,
                guiGroup: "reminderType",
                guiWidthInGroup: 35,
                hasLabel: false,
                minValue: -1,
                listItems: [
                    new FrontendFieldListItem(-1, app.t("entity.CalendarEventTemplate.other.reminderDeltaBefore")),
                    new FrontendFieldListItem(1, app.t("entity.CalendarEventTemplate.other.reminderDeltaAfter"))
                ],
                onValueChanged: () => ProjectPlanningTools.reminderDeltaGuiChanged(getForm())
            })
        ];
    }

    static updateReminderDeltaVisibility(form: FrontendForm): void {
        if (form == null) {
            return;
        }

        let visible = (form.getValue("reminderType") as CalendarEventReminderType) != CalendarEventReminderType.none;
        form.getField("reminderDeltaDirection").visible = visible;
        form.getField("reminderDeltaUnit").visible = visible;
        form.getField("reminderDeltaAmount").visible = visible;
    }

    static reminderDeltaMinutesChanged(form: FrontendForm): void {
        if (form == null || ProjectPlanningTools.updatingReminderDeltaForms.has(form)) {
            return;
        }

        ProjectPlanningTools.updatingReminderDeltaForms.add(form);
        try {
            let deltaMinutes = form.getValueNumber("reminderDeltaMinutes") ?? 0;
            let amountMinutes = Math.abs(deltaMinutes);
            let unit = ProjectPlanningTools.getBestReminderDeltaUnit(amountMinutes);

            form.setValue("reminderDeltaDirection", deltaMinutes <= 0 ? -1 : 1);
            form.setValue("reminderDeltaUnit", unit);
            form.setValue("reminderDeltaAmount", Utils.divSafe(amountMinutes, ProjectPlanningTools.getReminderDeltaUnitFactor(unit)));
        }
        finally {
            ProjectPlanningTools.updatingReminderDeltaForms.delete(form);
        }
    }

    static reminderDeltaGuiChanged(form: FrontendForm): void {
        if (form == null || ProjectPlanningTools.updatingReminderDeltaForms.has(form)) {
            return;
        }

        ProjectPlanningTools.updatingReminderDeltaForms.add(form);
        try {
            let direction = form.getValueNumber("reminderDeltaDirection") ?? -1;
            let unit = (form.getValue("reminderDeltaUnit") as ReminderDeltaUnit) ?? "minutes";
            let amount = form.getValueNumber("reminderDeltaAmount") ?? 0;
            form.setValue("reminderDeltaMinutes", direction * amount * ProjectPlanningTools.getReminderDeltaUnitFactor(unit));
        }
        finally {
            ProjectPlanningTools.updatingReminderDeltaForms.delete(form);
        }
    }

    private static getBestReminderDeltaUnit(minutes: number): ReminderDeltaUnit {
        if (minutes != 0 && minutes % 10080 == 0) {
            return "weeks";
        }
        if (minutes != 0 && minutes % 1440 == 0) {
            return "days";
        }
        if (minutes != 0 && minutes % 60 == 0) {
            return "hours";
        }
        return "minutes";
    }

    private static getReminderDeltaUnitFactor(unit: ReminderDeltaUnit): number {
        switch (unit) {
            case "weeks":
                return 10080;
            case "days":
                return 1440;
            case "hours":
                return 60;
            default:
                return 1;
        }
    }

}
